import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { createWorkflowSchedulesSchema, CreateWorkflowSchedulesSchema } from "../../services/workflowSchedules.schema";
import AccordionMaintenanceWorkflowSchedulesOverview from "../../components/AccordionOverview";
import { maintenanceWorkflowSchedulesKindOptions } from "../../constants/kind.constant";
import AccordionMaintenanceWorkflowSchedulesEquipments from "../../components/AccordionEquipments";
import AccordionMaintenanceWorkflowSchedulesExecution from "../../components/AccordionExecution";
import AccordionMaintenanceWorkflowSchedulesObjectives from "../../components/AccordionObjectives";
import useCreateMaintenanceWorkflowSchedule from "../../hooks/useCreateWorkflowSchedules";

const Create = () => {
  const goBack = useGoBack();
  const queryClient = useQueryClient()

  const { data: companyData } = useMineCompany();

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToCreateMaintenanceWorkflowSchedules =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::WorkflowSchedules::Create')

  const {
    mutate: createMaintenanceWorkflowSchedule,
    isPending: createMaintenanceWorkflowScheduleIsPending
  } = useCreateMaintenanceWorkflowSchedule({
    companyData,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['maintenanceWorkflowSchedules']})
      goBack({fallback: '/maintenance-workflows-schedules'})
    },
    onError: (error: any) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  })

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<CreateWorkflowSchedulesSchema>({
    resolver: zodResolver(createWorkflowSchedulesSchema)
  });

  const onSubmit: SubmitHandler<CreateWorkflowSchedulesSchema> = (data) => createMaintenanceWorkflowSchedule(data);

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: '/maintenance-workflows-schedules' }}
        title="Programação de Manutenção"
        subtitle="Nova Programação"
        buttons={
          userHasPermissionToCreateMaintenanceWorkflowSchedules ? (
            <Button
              type="submit"
              name="submit"
            >
              Criar programação
            </Button>
          ) : undefined
        }
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToCreateMaintenanceWorkflowSchedules ? 200 : 403}>
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionMaintenanceWorkflowSchedulesOverview />

          <div className="flex gap-2">
            <TextField
              label="Nome"
              markAsRequired
              errorMessage={errors?.display_name?.message}
              {...register('display_name')}
              className="flex-1"
              maxLength={32}
              disabled={createMaintenanceWorkflowScheduleIsPending}
            />
            <SelectController
              control={control}
              name="kind"
              label="Tipo de manutenção"
              className="flex-1"
              markAsRequired
              options={maintenanceWorkflowSchedulesKindOptions}
              errorMessage={errors.kind?.message || undefined}
              isDisabled={createMaintenanceWorkflowScheduleIsPending}
            />
          </div>

          <TextArea
            label="Descrição (será exibida na solicitação de manutenção)"
            {...register('observations')}
            markAsRequired
            errorMessage={errors.observations?.message || undefined}
            rows={6}
            disabled={createMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesEquipments
            control={control}
            errors={errors}
            disabled={createMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesExecution
            control={control}
            errors={errors}
            disabled={createMaintenanceWorkflowScheduleIsPending}
          />

          <AccordionMaintenanceWorkflowSchedulesObjectives
            control={control}
            errors={errors}
            disabled={createMaintenanceWorkflowScheduleIsPending}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Create;